/**
 * Catalog Sort & Filter functionality
 * Handles sorting and filtering for collection/catalog pages
 * Uses event delegation to work after AJAX/facet updates
 */

(function() {
  // Prevent multiple initializations
  if (window.__catalogInitialized) return;
  window.__catalogInitialized = true;

  /**
   * Unlock body scroll after filter/sort operations
   */
  function unlockBodyScroll() {
    document.body.classList.remove('not-scroll', 'no-scroll');
    document.body.style.paddingRight = '';
    const header = document.querySelector('header');
    if (header) {
      header.style.paddingRight = '';
    }
  }

  /**
   * Close filter modal
   */
  function closeFilterModal() {
    const filterModal = document.getElementById('filterModal');
    if (filterModal) {
      filterModal.classList.remove('open');
    }
    unlockBodyScroll();
  }

  /**
   * Apply sorting by updating URL and triggering facet:update
   */
  function applySorting(sortValue, sortItem) {
    const url = new URL(window.location.href);
    
    if (sortValue && sortValue !== '') {
      url.searchParams.set('sort_by', sortValue);
    } else {
      url.searchParams.delete('sort_by');
    }
    
    // Remove page parameter when sorting changes
    url.searchParams.delete('page');
    
    // Find section ID for facet update
    const sectionElement = sortItem.closest('.shopify-section');
    const sectionId = sectionElement ? sectionElement.id.replace('shopify-section-', '') : null;
    
    if (sectionId) {
      // Use facet:update for AJAX navigation
      url.searchParams.set('section_id', sectionId);
      document.dispatchEvent(new CustomEvent('facet:update', {
        bubbles: true,
        detail: { url }
      }));
    } else {
      // Fallback to full page navigation
      window.location.href = url.toString();
    }
  }

  /**
   * Update clear button state based on active filters
   */
  function updateClearButtonState(filterModal) {
    const clearButton = filterModal.querySelector('.modal__actions .button:not(.button--transparent)');
    if (!clearButton) return;
    
    const checkedCount = filterModal.querySelectorAll('input[type="checkbox"]:checked').length;
    const priceInputs = filterModal.querySelectorAll('input[type="number"]');
    let hasPriceFilter = false;
    
    priceInputs.forEach(input => {
      if (input.value && input.value !== '') {
        hasPriceFilter = true;
      }
    });
    
    if (checkedCount > 0 || hasPriceFilter) {
      clearButton.classList.remove('disabled');
      clearButton.removeAttribute('disabled');
    } else {
      clearButton.classList.add('disabled');
      clearButton.setAttribute('disabled', '');
    }
  }

  /**
   * Update results count in filter modal
   */
  async function updateResultsCount(filterModal) {
    const filterForm = filterModal.querySelector('form.facets');
    const applyButton = filterModal.querySelector('.button--apply-filters');
    const resultsCountSpan = applyButton?.querySelector('[data-results-count]');
    
    if (!resultsCountSpan || !filterForm) return;
    
    const formData = new FormData(filterForm);
    const params = new URLSearchParams(formData);
    
    const sectionWrapper = filterForm.closest('facets-form');
    if (sectionWrapper) {
      params.append('section_id', sectionWrapper.getAttribute('section-id'));
    }
    
    const url = `${filterForm.action}?${params.toString()}`;
    
    try {
      const response = await fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      
      if (!response.ok) return;
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      const totalCountElement = doc.querySelector('[data-products-range-total]');
      const countElement = doc.querySelector('[data-products-count]')
        || doc.querySelector('.product-list__grid')
        || doc.querySelector('.collection-products');
      
      let count = 0;
      if (totalCountElement) {
        const totalCount = parseInt(totalCountElement.getAttribute('data-products-range-total'), 10);
        count = Number.isFinite(totalCount) ? totalCount : 0;
      } else if (countElement) {
        if (countElement.hasAttribute('data-products-count')) {
          count = parseInt(countElement.getAttribute('data-products-count'), 10);
        } else {
          const productCards = countElement.querySelectorAll('.product-card, [data-product-card]');
          count = productCards.length;
        }
      }
      
      const currentText = resultsCountSpan.textContent;
      const match = currentText.match(/\[(\d+)\]/);
      
      if (match) {
        resultsCountSpan.textContent = currentText.replace(/\[\d+\]/, `[${count}]`);
      } else {
        resultsCountSpan.textContent = currentText.replace(/\d+/, count);
      }
    } catch (error) {
      // Silent fail - don't interrupt user experience
    }
  }

  // Event delegation for sort items - works after DOM updates
  document.addEventListener('click', (e) => {
    const sortItem = e.target.closest('[data-sort-item]');
    if (!sortItem) return;
    
    e.preventDefault();
    const sortValue = sortItem.getAttribute('data-sort-value');
    applySorting(sortValue, sortItem);
  });

  // Event delegation for filter modal checkbox changes
  document.addEventListener('change', (e) => {
    const filterModal = e.target.closest('#filterModal');
    if (!filterModal) return;
    
    if (e.target.type === 'checkbox') {
      updateClearButtonState(filterModal);
      updateResultsCount(filterModal);
    }
  });

  // Event delegation for filter modal number input changes
  document.addEventListener('input', (e) => {
    const filterModal = e.target.closest('#filterModal');
    if (!filterModal) return;
    
    if (e.target.type === 'number') {
      updateClearButtonState(filterModal);
      updateResultsCount(filterModal);
    }
  });

  // Toggle sort dropdown visibility
  document.addEventListener('click', (e) => {
    const sortBtn = e.target.closest('.sort-btn');
    const catalogSort = document.querySelector('[data-catalog-sort]');
    
    if (sortBtn && catalogSort) {
      e.preventDefault();
      catalogSort.classList.toggle('active');
      return;
    }
    
    // Close dropdown when clicking outside
    if (catalogSort && !e.target.closest('[data-catalog-sort]')) {
      catalogSort.classList.remove('active');
    }
  });

  /**
   * The page shows one product counter, beside the title. The title lives in another section,
   * so after filtering its number is taken from the refreshed catalogue section.
   */
  function syncHeadingCount() {
    const carrier = document.querySelector('.section-catalog [data-products-range-total]');
    const headingCount = document.querySelector('.heading-section .page-title sup');
    if (!carrier || !headingCount) return;

    const total = parseInt(carrier.getAttribute('data-products-range-total'), 10);
    if (!Number.isFinite(total)) return;

    const text = `(${total})`;
    if (headingCount.textContent !== text) {
      headingCount.textContent = text;
    }
  }

  const catalogSection = document.querySelector('.section-catalog');
  if (catalogSection) {
    new MutationObserver(syncHeadingCount).observe(catalogSection, { childList: true });
  }

  // Listen for facet:update to close modal and unlock scroll
  document.addEventListener('facet:update', () => {
    // Close filter modal and unlock body scroll when facets are updated
    closeFilterModal();
  });

  // Initialize clear button state on page load
  function initFilterState() {
    const filterModal = document.getElementById('filterModal');
    if (filterModal) {
      updateClearButtonState(filterModal);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFilterState);
  } else {
    initFilterState();
  }
})();
